import {
	QueryKey,
	UseQueryOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { useCookies } from 'react-cookie';

import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IUser } from '@/interfaces/api/IUser';
import { StoredCookies } from '@/interfaces/auth/cookies.enum';
import { haggleApiService } from '@/services/haggle.service';
import { subscriptionService } from '@/services/subscription.service';

type ICustomQueryOptions = Omit<
	UseQueryOptions<IUser, Error, IUser, QueryKey>,
	'queryKey' | 'queryFn' | 'refetchOnWindowFocus' | 'initialData'
>;

export const useUser = (options?: ICustomQueryOptions) => {
	const queryClient = useQueryClient();
	const ONE_HOUR = 1000 * 60 * 60;

	const [cookies] = useCookies([
		StoredCookies.USERNAME,
		StoredCookies.REFRESH_TOKEN,
	]);

	const isAuthenticated =
		!!cookies[StoredCookies.REFRESH_TOKEN] && !!cookies[StoredCookies.USERNAME];

	const {
		data: userInfo,
		isLoading: queryIsLoading,
		refetch,
		...rest
	} = useQuery<IUser>({
		queryKey: [ApiQueryKeys.userInfo],
		queryFn: () =>
			isAuthenticated
				? haggleApiService.getMe()
				: Promise.reject('Not authenticated'),
		enabled: isAuthenticated,
		refetchOnWindowFocus: false,
		initialData:
			queryClient.getQueryData<IUser>([ApiQueryKeys.userInfo]) ?? undefined,
		staleTime: ONE_HOUR,
		...options,
	});

	const isLoading = !isAuthenticated || queryIsLoading;
	const setUserInfo = (user: IUser | undefined) => {
		queryClient.setQueryData<IUser | undefined>([ApiQueryKeys.userInfo], user);
	};

	return {
		user: userInfo,
		isLoading,
		setUserInfo,
		refetch,
		queryIsLoading,
		...rest,
	};
};

export const useUpdateRoles = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			user,
			store,
			planData,
		}: {
			user: any;
			store: any;
			planData: any;
		}) => {
			return await subscriptionService.updateRole(user, store, planData);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [ApiQueryKeys.userInfo],
			});
		},
	});
};
